import React, { useMemo } from "react";
import { Avatar, Badge, Box, HStack, Image, Stack, Text, Spinner, useBreakpointValue } from "@chakra-ui/react";
import { FaVideo } from "react-icons/fa";

const hub = { x: 50, y: 55 };
const MAX_SCENE_USERS = 50;

const isCameraActive = (camera) => {
  if (!camera) return false;
  if (camera.ativa === undefined || camera.ativa === null || camera.ativa === "") {
    return true;
  }
  const normalized = String(camera.ativa).trim().toLowerCase();
  return Number(camera.ativa) === 1 || camera.ativa === true || ["ativo", "true", "sim"].includes(normalized);
};

const polarToPoint = (angleDeg, radiusX, radiusY, center = hub) => {
  const angle = (angleDeg * Math.PI) / 180;
  return {
    x: center.x + Math.cos(angle) * radiusX,
    y: center.y + Math.sin(angle) * radiusY
  };
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

const createCurvePath = (from, to, bend = 0.18) => {
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const cx = midX - dy * bend;
  const cy = midY + dx * bend;
  return `M ${from.x} ${from.y} Q ${cx} ${cy} ${to.x} ${to.y}`;
};

const ringConfigForCount = (count, isMobile) => {
  const capacities = isMobile ? [6, 8, 10, 12, 14] : [10, 12, 14, 14];
  const rings = [];
  let remaining = Math.max(0, count);

  capacities.forEach((cap) => {
    if (!remaining) return;
    const take = Math.min(cap, remaining);
    rings.push(take);
    remaining -= take;
  });

  if (remaining > 0) {
    rings.push(remaining);
  }

  return rings;
};

const spreadNodes = (nodes, isMobile) => {
  if (!nodes.length) return nodes;

  const spreaded = nodes.map((node) => ({
    ...node,
    position: { ...node.position }
  }));

  const minX = isMobile ? 7 : 8;
  const maxX = isMobile ? 93 : 92;
  const minY = isMobile ? 9 : 8;
  const maxY = isMobile ? 90 : 90;
  const baseMinDistance = isMobile ? 13 : 10.8;
  const densityPenalty = Math.max(0, spreaded.length - (isMobile ? 14 : 20)) * (isMobile ? 0.12 : 0.09);
  const minDistance = Math.max(isMobile ? 6.8 : 6.1, baseMinDistance - densityPenalty);
  const hubClearance = Math.max(isMobile ? 10.2 : 8.6, minDistance * 0.95);

  for (let iteration = 0; iteration < 120; iteration += 1) {
    let moved = false;

    for (let i = 0; i < spreaded.length; i += 1) {
      for (let j = i + 1; j < spreaded.length; j += 1) {
        const a = spreaded[i].position;
        const b = spreaded[j].position;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.hypot(dx, dy);

        if (dist >= minDistance) continue;

        const overlap = (minDistance - Math.max(dist, 0.01)) / 2;
        const angle = dist > 0.01 ? Math.atan2(dy, dx) : ((i * 37 + j * 17) * Math.PI) / 180;
        const pushX = Math.cos(angle) * overlap;
        const pushY = Math.sin(angle) * overlap;

        a.x -= pushX;
        a.y -= pushY;
        b.x += pushX;
        b.y += pushY;
        moved = true;
      }
    }

    spreaded.forEach((node) => {
      const dx = node.position.x - hub.x;
      const dy = node.position.y - hub.y;
      const dist = Math.hypot(dx, dy);
      if (dist < hubClearance) {
        const angle = dist > 0.01 ? Math.atan2(dy, dx) : Math.PI / 2;
        node.position.x = hub.x + Math.cos(angle) * hubClearance;
        node.position.y = hub.y + Math.sin(angle) * hubClearance;
        moved = true;
      }

      node.position.x = clamp(node.position.x, minX, maxX);
      node.position.y = clamp(node.position.y, minY, maxY);
    });

    if (!moved) break;
  }

  return spreaded;
};

const buildNodeLayout = (users, isMobile) => {
  const sliced = (users || []).slice(0, MAX_SCENE_USERS);
  const rings = ringConfigForCount(sliced.length, isMobile);
  const positioned = [];
  let cursor = 0;

  rings.forEach((ringCount, ringIndex) => {
    if (!ringCount) return;
    const startAngle = isMobile ? -166 + ringIndex * 8 : -160 + ringIndex * 12;
    const sweep = isMobile ? 334 : 330;
    const step = ringCount > 1 ? sweep / (ringCount - 1) : 0;
    const radiusX = isMobile ? 18 + ringIndex * 7 : 22 + ringIndex * 7.5;
    const radiusY = isMobile ? 24 + ringIndex * 8 : 18 + ringIndex * 7.8;

    for (let i = 0; i < ringCount && cursor < sliced.length; i += 1) {
      const angle = ringCount === 1 ? 0 : startAngle + step * i;
      const point = polarToPoint(angle, radiusX, radiusY);
      positioned.push({
        ...sliced[cursor],
        position: {
          x: clamp(point.x, isMobile ? 7 : 8, isMobile ? 93 : 92),
          y: clamp(point.y, isMobile ? 9 : 8, isMobile ? 90 : 90)
        }
      });
      cursor += 1;
    }
  });

  return spreadNodes(positioned, isMobile);
};

const buildEdges = (nodes) => {
  const hubEdges = nodes.map((node, index) => ({
    id: `hub-${node.usuario_id}`,
    path: createCurvePath(hub, node.position, index % 2 === 0 ? 0.08 : -0.08)
  }));

  const meshEdges = [];
  nodes.forEach((node) => {
    const nearest = nodes
      .filter((candidate) => candidate.usuario_id !== node.usuario_id)
      .map((candidate) => ({
        normalizedId: [node.usuario_id, candidate.usuario_id].sort().join("-"),
        from: node.position,
        to: candidate.position,
        dist: distance(node.position, candidate.position)
      }))
      .sort((a, b) => a.dist - b.dist)
      .slice(0, 2);

    nearest.forEach((edge, edgeIndex) => {
      if (meshEdges.some((item) => item.normalizedId === edge.normalizedId)) return;
      meshEdges.push({
        id: `mesh-${edge.normalizedId}`,
        normalizedId: edge.normalizedId,
        path: createCurvePath(edge.from, edge.to, edgeIndex === 0 ? 0.04 : -0.04)
      });
    });
  });

  return { hubEdges, meshEdges };
};

const ImageNetworkScene = ({
  backgroundUrl,
  title,
  subtitle,
  cityLabel,
  users,
  apiBase,
  onSelectUser,
  panicUserIds,
  ocorrenciaUserIds,
  accentColor = "#7dd3fc",
  sceneHeight,
  loading
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const limitedUsers = useMemo(() => (users || []).slice(0, MAX_SCENE_USERS), [users]);
  const nodes = useMemo(() => buildNodeLayout(limitedUsers, Boolean(isMobile)), [limitedUsers, isMobile]);
  const { hubEdges, meshEdges } = useMemo(() => buildEdges(nodes), [nodes]);
  const avatarSize = useMemo(() => {
    if (isMobile) {
      if (nodes.length > 24) return "sm";
      return "md";
    }
    if (nodes.length > 38) return "md";
    if (nodes.length > 24) return "lg";
    return "xl";
  }, [isMobile, nodes.length]);
  const nodePadding = useMemo(() => {
    if (isMobile) return nodes.length > 20 ? "4px" : "6px";
    return nodes.length > 30 ? "6px" : "9px";
  }, [isMobile, nodes.length]);
  const avatarBorder = useMemo(() => {
    if (isMobile && nodes.length > 18) return "2.25px solid white";
    return isMobile ? "2.5px solid white" : "3px solid white";
  }, [isMobile, nodes.length]);
  const totalCameras = useMemo(
    () => {
      const uniqueCameraKeys = new Set();
      limitedUsers.forEach((user) => {
        (user.cameras || []).forEach((camera) => {
          if (!isCameraActive(camera)) return;
          const cameraId = Number(camera?.id);
          if (Number.isFinite(cameraId) && cameraId > 0) {
            uniqueCameraKeys.add(`id:${cameraId}`);
            return;
          }
          const canal = camera?.canal ?? "";
          const rtmp = camera?.rtmp_link ?? "";
          const nome = camera?.nome ?? "";
          uniqueCameraKeys.add(`u:${user?.usuario_id || "x"}|c:${canal}|r:${rtmp}|n:${nome}`);
        });
      });
      return uniqueCameraKeys.size;
    },
    [limitedUsers]
  );

  return (
    <Box
      position="relative"
      h={sceneHeight || "calc(100vh - 144px)"}
      minH={sceneHeight || "calc(100vh - 144px)"}
      borderRadius={{ base: "0", md: "32px" }}
      overflow="hidden"
      bg="#05251f"
      boxShadow="0 30px 80px rgba(3, 22, 18, 0.28)"
    >
      <Box
        position="absolute"
        inset="0"
        bgImage={`linear-gradient(180deg, rgba(3, 22, 18, 0.08), rgba(3, 22, 18, 0.72)), url('${backgroundUrl || "/sistema/fundo.png"}')`}
        bgSize="cover"
        bgPosition="center"
      />
      <Box
        position="absolute"
        inset="0"
        bg="radial-gradient(circle at center, rgba(125,211,252,0.18), transparent 42%), linear-gradient(180deg, rgba(3,22,18,0.08), rgba(4,15,13,0.60))"
      />

      <svg className="network-grid" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <filter id="networkGlow">
            <feGaussianBlur stdDeviation="0.35" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {meshEdges.map((edge) => (
          <path
            key={edge.id}
            d={edge.path}
            fill="none"
            stroke="rgba(225,247,255,0.34)"
            strokeWidth="0.14"
            filter="url(#networkGlow)"
          />
        ))}

        {hubEdges.map((edge, index) => (
          <path
            key={edge.id}
            d={edge.path}
            fill="none"
            stroke={accentColor}
            strokeOpacity={index % 3 === 0 ? "0.92" : "0.74"}
            strokeWidth={index % 2 === 0 ? "0.28" : "0.22"}
            filter="url(#networkGlow)"
          />
        ))}

        {nodes.map((node) => (
          <circle
            key={`dot-${node.usuario_id}`}
            cx={node.position.x}
            cy={node.position.y}
            r="0.34"
            fill="#dffbff"
            opacity="0.95"
          />
        ))}
      </svg>

      <Box position="relative" zIndex="2" h="100%">
        <Stack spacing={1} px={{ base: 4, md: 8 }} pt={{ base: 5, md: 7 }} maxW={{ base: "56%", md: "560px" }}>
          <Badge
            alignSelf="flex-start"
            px={3}
            py={1}
            borderRadius="full"
            bg="rgba(255,255,255,0.14)"
            color="white"
            fontSize="0.7rem"
            letterSpacing="0.12em"
          >
            Rede rural de vigilancia
          </Badge>
          <Text color="white" fontSize={{ base: "2xl", md: "4xl" }} fontWeight="900" lineHeight="1">
            {title || cityLabel || "Rede Inteligente"}
          </Text>
          <Text color="whiteAlpha.900" fontSize={{ base: "sm", md: "md" }} maxW="460px">
            {subtitle || "Visualizacao por cidade com conexoes dinamicas entre clientes e assinantes."}
          </Text>
        </Stack>

        <Box position="absolute" inset="0">
          {nodes.map((user) => {
            const hasPanic = panicUserIds?.has(Number(user.usuario_id));
            const hasEvent = ocorrenciaUserIds?.has(Number(user.usuario_id));
            return (
              <Box
                key={user.usuario_id}
                position="absolute"
                left={`${user.position.x}%`}
                top={`${user.position.y}%`}
                transform="translate(-50%, -50%)"
                cursor="pointer"
                onClick={() => onSelectUser?.(user.usuario_id)}
              >
                <Box className={`network-node ${hasPanic ? "is-alert" : ""}`} style={{ padding: nodePadding }}>
                  <Avatar
                    size={avatarSize}
                    src={`${apiBase}/usuarios/${user.usuario_id}.png`}
                    name={user.nome || "Cliente"}
                    border={avatarBorder}
                    boxShadow="0 12px 32px rgba(0,0,0,0.35)"
                  />
                  {hasEvent && !hasPanic ? <Box className="network-dot" /> : null}
                  {hasPanic ? <Box className="network-dot network-dot--danger" /> : null}
                </Box>
              </Box>
            );
          })}

          <Box className="network-hub">
          <Box className="network-hub__shield">
              {loading ? (
                <Stack spacing={2} align="center">
                  <Spinner color="whiteAlpha.900" />
                  <Text fontSize="sm" color="whiteAlpha.900">
                    Carregando
                  </Text>
                </Stack>
              ) : (
                <>
                  <Text fontSize={{ base: "2xl", md: "4xl" }} fontWeight="900" lineHeight="1">
                    {totalCameras}
                  </Text>
                  <Box
                    mt={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize={{ base: "18px", md: "24px" }}
                  >
                    <FaVideo />
                  </Box>
                </>
              )}
              <HStack spacing={1} justify="center" mt={2}>
                <Box className="hub-mini-icon" />
                <Box className="hub-mini-icon" />
                <Box className="hub-mini-icon" />
              </HStack>
            </Box>
          </Box>
        </Box>

        <Box position="absolute" left="0" right="0" bottom="0" p={{ base: 4, md: 7 }}>
          <HStack justify="space-between" align="flex-end" flexWrap="wrap" gap={3}>
            <Text color="white" fontWeight="800" fontSize={{ base: "xl", md: "3xl" }}>
              {cityLabel || "Cidade configurada"}
            </Text>
            <HStack
              spacing={3}
              px={4}
              py={2}
              borderRadius="full"
              bg="rgba(255,255,255,0.12)"
              backdropFilter="blur(8px)"
            >
              <Text color="whiteAlpha.900" fontSize="sm">
                Visual por imagem
              </Text>
              <Image src="/sistema/logo.png" alt="Logo" boxSize="24px" objectFit="contain" />
            </HStack>
          </HStack>
        </Box>
      </Box>
    </Box>
  );
};

export default ImageNetworkScene;
